import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Tabs, Tab } from "@mui/material";
import { useAuth } from "../context/AuthContext";

import Dashboard from "../components/management/Dashboard";
import Users from "../components/management/Users";
import Properties from "../components/management/Properties";
import Steps from "../components/management/Steps";
import Reports from "../components/management/Reports";
import Issues from "../components/management/Issues";
import AuditLogs from "../components/management/AuditLogs";
import Notifications from "../components/management/Notifications";
import Calendar from "../components/management/Calendar";
import Rota from "../components/management/Rota";
import UserAvailability from "../components/management/UserAvailability";
import Chat from "../components/management/Chat";

import "../styles/Management.css";

const sections = {
  Chat,
  Dashboard,
  Users,
  Properties,
  Steps,
  Reports,
  Issues,
  AuditLogs,
  Notifications,
  Calendar,
  Rota,
  UserAvailability,
};

export default function Management() {
  const sectionKeys = Object.keys(sections);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [activeIndex, setActiveIndex] = useState(() => {
    const openTab = searchParams.get("openTab");
    return openTab && sectionKeys.includes(openTab)
      ? sectionKeys.indexOf(openTab)
      : sectionKeys.indexOf("Dashboard");
  });

  const activeSection = sectionKeys[activeIndex];
  const ActiveComponent = sections[activeSection];
  const { user } = useAuth();
  const companyId = user?.USER_COMPANY_ID || "";

  const [unreadCount, setUnreadCount] = useState(0);
  const lastUnreadCountRef = useRef(0);

  // Ask for notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission().then((permission) => {
        console.log("🔔 Notification permission:", permission);
      });
    }
  }, []);

  // Poll for unread messages
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE}/app/API/rbmgt/chat/getUnreadCounts.php`);
        const data = await res.json();

        //console.log("📨 Unread API response:", data);

        if (data.success && data.counts) {
          const total = Object.values(data.counts).reduce((sum, n) => sum + Number(n), 0);

          if (total > lastUnreadCountRef.current) {
            new Audio("/sounds/notification.mp3").play().catch(() => {});

            if ("Notification" in window && Notification.permission === "granted") {
              const notification = new Notification("📩 New Guest Message", {
                body: `You have ${total} unread message${total > 1 ? "s" : ""}.`,
                icon: "/icon-512.png",
                tag: "chat-notification",
              });

              notification.onclick = () => {
                window.focus();
                setActiveIndex(sectionKeys.indexOf("Chat"));
              };
            }
          }

          lastUnreadCountRef.current = total;
          setUnreadCount(total);
        }
      } catch (err) {
        console.error("❌ Failed to fetch unread counts", err);
      }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [sectionKeys]);

  return (
    <div className="management-container">
      <div className="management-tabs">
        <Tabs
          value={activeIndex}
          onChange={(_, newIndex) => setActiveIndex(newIndex)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {sectionKeys.map((key) => (
            <Tab
              key={key}
              label={
                key === "Chat" && unreadCount > 0 ? (
                  <span className="tab-label-with-badge">
                    Chat <span className="tab-unread-badge">{unreadCount}</span>
                  </span>
                ) : (
                  key
                )
              }
            />
          ))}
        </Tabs>
      </div>

      <div className="management-panel">
        {activeSection === "Rota" ? (
          <Rota adminCompany={companyId} />
        ) : (
          <ActiveComponent />
        )}
      </div>
    </div>
  );
}
